import type { Role, Faction } from "../data/types";

// Procedural sprite + strategic-icon drawing. No external assets: every unit
// role gets a distinct silhouette. Vantage = angular / pale gunmetal / cyan;
// Feral = rounded / rust-bronze / red. All sprites are drawn facing +x; the
// renderer rotates the context to the unit's heading first.

export interface Palette {
  hull: string; // body fill
  dark: string; // shadow / underside
  accent: string; // faction accent (cyan / orange)
  tint: string; // player colour
  glass: string; // cockpit / glow
}

export const VANTAGE_PALETTE: Palette = {
  hull: "#aab8c4",
  dark: "#5c6b78",
  accent: "#39d6ff",
  tint: "#40e0ff",
  glass: "#bff4ff",
};

export const FERAL_PALETTE: Palette = {
  hull: "#b07f4f",
  dark: "#5f3f28",
  accent: "#ff7a2a",
  tint: "#ff5a3c",
  glass: "#ffd0a0",
};

export function paletteFor(faction: Faction): Palette {
  return faction === "feral" ? FERAL_PALETTE : VANTAGE_PALETTE;
}

function poly(ctx: CanvasRenderingContext2D, pts: number[][]): void {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
}

/** Draw a unit/building sprite centred at the origin, facing +x, sized to `r`. */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  role: Role,
  faction: Faction,
  r: number,
  pal: Palette,
): void {
  const angular = faction !== "feral";
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(0.6, r * 0.09);
  switch (role) {
    case "commander": drawCommander(ctx, r, pal, angular); break;
    case "constructor": drawConstructor(ctx, r, pal, angular); break;
    case "scout": drawScout(ctx, r, pal, angular); break;
    case "raider": drawRaider(ctx, r, pal, angular); break;
    case "assault": drawAssault(ctx, r, pal, angular); break;
    case "rocket": drawRocket(ctx, r, pal, angular); break;
    case "tank": drawTank(ctx, r, pal, angular); break;
    case "aa": drawAA(ctx, r, pal, angular); break;
    case "sniper": drawSniper(ctx, r, pal, angular); break;
    case "artillery": drawArtillery(ctx, r, pal, angular); break;
    case "bomb": drawBomb(ctx, r, pal); break;
    case "factory": drawFactory(ctx, r, pal, angular); break;
    case "extractor": drawExtractor(ctx, r, pal, angular); break;
    case "solar": drawSolar(ctx, r, pal, angular); break;
    case "wind": drawWind(ctx, r, pal); break;
    case "converter": drawConverter(ctx, r, pal, angular); break;
    case "storage": drawStorage(ctx, r, pal, angular); break;
    case "turret": drawTurret(ctx, r, pal, angular); break;
    case "tree": drawTree(ctx, r); break;
    case "rock": drawRock(ctx, r); break;
    default: drawRaider(ctx, r, pal, angular); break;
  }
}

function body(ctx: CanvasRenderingContext2D, pal: Palette): void {
  ctx.fillStyle = pal.hull;
  ctx.strokeStyle = pal.dark;
  ctx.fill();
  ctx.stroke();
}

// ---- Units ----

function drawCommander(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  // Shoulder pods.
  ctx.fillStyle = pal.dark;
  if (ang) {
    poly(ctx, [[-r * 0.4, -r], [r * 0.2, -r * 1.1], [r * 0.2, -r * 0.5], [-r * 0.4, -r * 0.5]]); ctx.fill();
    poly(ctx, [[-r * 0.4, r], [r * 0.2, r * 1.1], [r * 0.2, r * 0.5], [-r * 0.4, r * 0.5]]); ctx.fill();
  } else {
    ctx.beginPath(); ctx.ellipse(-r * 0.1, -r * 0.85, r * 0.5, r * 0.4, 0, 0, 7); ctx.fill();
    ctx.beginPath(); ctx.ellipse(-r * 0.1, r * 0.85, r * 0.5, r * 0.4, 0, 0, 7); ctx.fill();
  }
  // Torso.
  if (ang) poly(ctx, [[-r, -r * 0.7], [r * 0.5, -r * 0.85], [r * 1.15, 0], [r * 0.5, r * 0.85], [-r, r * 0.7], [-r * 0.7, 0]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 1.05, r * 0.85, 0, 0, 7); }
  body(ctx, pal);
  // Head / eye.
  ctx.fillStyle = pal.tint;
  ctx.beginPath(); ctx.arc(r * 0.45, 0, r * 0.32, 0, 7); ctx.fill();
  ctx.fillStyle = pal.glass;
  ctx.beginPath(); ctx.arc(r * 0.5, 0, r * 0.15, 0, 7); ctx.fill();
  // Main gun.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.28;
  ctx.beginPath(); ctx.moveTo(r * 0.4, -r * 0.25); ctx.lineTo(r * 1.5, -r * 0.25); ctx.stroke();
}

function drawConstructor(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[-r * 0.9, -r * 0.7], [r * 0.7, -r * 0.7], [r * 0.9, 0], [r * 0.7, r * 0.7], [-r * 0.9, r * 0.7]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 0.9, r * 0.75, 0, 0, 7); }
  body(ctx, pal);
  // Nanolathe arm + emitter.
  ctx.strokeStyle = pal.accent; ctx.lineWidth = r * 0.18;
  ctx.beginPath(); ctx.moveTo(r * 0.2, 0); ctx.lineTo(r * 1.3, -r * 0.5); ctx.stroke();
  ctx.fillStyle = pal.glass;
  ctx.beginPath(); ctx.arc(r * 1.3, -r * 0.5, r * 0.22, 0, 7); ctx.fill();
  // Cab.
  ctx.fillStyle = pal.dark;
  ctx.beginPath(); ctx.arc(-r * 0.1, 0, r * 0.3, 0, 7); ctx.fill();
}

function drawScout(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[r * 1.2, 0], [-r * 0.7, -r * 0.55], [-r * 0.4, 0], [-r * 0.7, r * 0.55]]);
  else { ctx.beginPath(); ctx.moveTo(r * 1.2, 0); ctx.quadraticCurveTo(-r * 0.5, -r * 0.7, -r * 0.6, 0); ctx.quadraticCurveTo(-r * 0.5, r * 0.7, r * 1.2, 0); }
  body(ctx, pal);
  ctx.fillStyle = pal.tint;
  ctx.beginPath(); ctx.arc(r * 0.2, 0, r * 0.22, 0, 7); ctx.fill();
}

function drawRaider(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[r * 1.1, 0], [r * 0.1, -r * 0.75], [-r * 0.85, -r * 0.6], [-r * 0.85, r * 0.6], [r * 0.1, r * 0.75]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r, r * 0.78, 0, 0, 7); }
  body(ctx, pal);
  // Twin barrels.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.16;
  ctx.beginPath(); ctx.moveTo(r * 0.3, -r * 0.3); ctx.lineTo(r * 1.25, -r * 0.3);
  ctx.moveTo(r * 0.3, r * 0.3); ctx.lineTo(r * 1.25, r * 0.3); ctx.stroke();
  ctx.fillStyle = pal.tint; ctx.beginPath(); ctx.arc(-r * 0.1, 0, r * 0.2, 0, 7); ctx.fill();
}

function drawAssault(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[r * 0.9, -r * 0.5], [r * 1.0, r * 0.5], [-r * 0.9, r * 0.85], [-r * 1.0, -r * 0.85]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 1.0, r * 0.9, 0, 0, 7); }
  body(ctx, pal);
  // Heavy central cannon.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.32;
  ctx.beginPath(); ctx.moveTo(r * 0.2, 0); ctx.lineTo(r * 1.4, 0); ctx.stroke();
  ctx.fillStyle = pal.accent; ctx.beginPath(); ctx.arc(-r * 0.25, 0, r * 0.3, 0, 7); ctx.fill();
  ctx.fillStyle = pal.glass; ctx.beginPath(); ctx.arc(-r * 0.25, 0, r * 0.14, 0, 7); ctx.fill();
}

function drawRocket(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[r * 0.8, -r * 0.6], [r * 0.8, r * 0.6], [-r * 0.9, r * 0.75], [-r * 0.9, -r * 0.75]]);
  else { ctx.beginPath(); ctx.ellipse(-r * 0.05, 0, r * 0.9, r * 0.8, 0, 0, 7); }
  body(ctx, pal);
  // Rocket pod tubes (pointing forward).
  ctx.fillStyle = pal.dark;
  for (let i = -1; i <= 1; i++) {
    ctx.fillRect(r * 0.2, i * r * 0.4 - r * 0.14, r * 1.1, r * 0.28);
  }
  ctx.fillStyle = pal.accent;
  for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.arc(r * 1.3, i * r * 0.4, r * 0.1, 0, 7); ctx.fill(); }
}

function drawTank(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  // Tracks.
  ctx.fillStyle = pal.dark;
  ctx.fillRect(-r, -r, r * 2, r * 0.45);
  ctx.fillRect(-r, r * 0.55, r * 2, r * 0.45);
  // Hull.
  if (ang) poly(ctx, [[-r * 0.85, -r * 0.55], [r * 0.85, -r * 0.55], [r * 0.85, r * 0.55], [-r * 0.85, r * 0.55]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 0.85, r * 0.6, 0, 0, 7); }
  body(ctx, pal);
  // Turret + barrel.
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  ctx.beginPath(); ctx.arc(-r * 0.05, 0, r * 0.42, 0, 7); ctx.fill(); ctx.stroke();
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.22;
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r * 1.4, 0); ctx.stroke();
}

function drawAA(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[-r * 0.8, -r * 0.7], [r * 0.8, -r * 0.7], [r * 0.8, r * 0.7], [-r * 0.8, r * 0.7]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 0.8, r * 0.75, 0, 0, 7); }
  body(ctx, pal);
  // Twin upward flak barrels (angled).
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.18;
  ctx.beginPath();
  ctx.moveTo(0, -r * 0.15); ctx.lineTo(r * 1.1, -r * 0.7);
  ctx.moveTo(0, r * 0.15); ctx.lineTo(r * 1.1, r * 0.7);
  ctx.stroke();
  ctx.fillStyle = pal.accent; ctx.beginPath(); ctx.arc(-r * 0.2, 0, r * 0.22, 0, 7); ctx.fill();
}

function drawSniper(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  if (ang) poly(ctx, [[-r * 0.7, -r * 0.5], [r * 0.5, -r * 0.5], [r * 0.5, r * 0.5], [-r * 0.7, r * 0.5]]);
  else { ctx.beginPath(); ctx.ellipse(-r * 0.1, 0, r * 0.7, r * 0.55, 0, 0, 7); }
  body(ctx, pal);
  // Very long thin barrel.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.14;
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r * 1.9, 0); ctx.stroke();
  ctx.strokeStyle = pal.tint; ctx.lineWidth = r * 0.06;
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r * 1.9, 0); ctx.stroke();
  ctx.fillStyle = pal.glass; ctx.beginPath(); ctx.arc(-r * 0.3, 0, r * 0.18, 0, 7); ctx.fill();
}

function drawArtillery(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  // Squat body with big upward mortar.
  if (ang) poly(ctx, [[-r * 0.85, -r * 0.6], [r * 0.7, -r * 0.6], [r * 0.7, r * 0.6], [-r * 0.85, r * 0.6]]);
  else { ctx.beginPath(); ctx.ellipse(0, 0, r * 0.85, r * 0.7, 0, 0, 7); }
  body(ctx, pal);
  ctx.fillStyle = pal.dark;
  ctx.beginPath(); ctx.arc(r * 0.1, 0, r * 0.5, 0, 7); ctx.fill();
  // Wide mortar muzzle.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.4;
  ctx.beginPath(); ctx.moveTo(r * 0.1, 0); ctx.lineTo(r * 1.15, -r * 0.35); ctx.stroke();
  ctx.fillStyle = pal.accent;
  ctx.beginPath(); ctx.arc(r * 1.15, -r * 0.35, r * 0.22, 0, 7); ctx.fill();
}

function drawBomb(ctx: CanvasRenderingContext2D, r: number, pal: Palette): void {
  // Small round crawler with spikes + blinking core.
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.8, 0, 7); ctx.fill(); ctx.stroke();
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.12;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    ctx.beginPath(); ctx.moveTo(Math.cos(a) * r * 0.6, Math.sin(a) * r * 0.6);
    ctx.lineTo(Math.cos(a) * r * 1.15, Math.sin(a) * r * 1.15); ctx.stroke();
  }
  ctx.fillStyle = "#ff3020";
  ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, 7); ctx.fill();
}

// ---- Buildings ----

function drawFactory(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  ctx.fillStyle = pal.dark;
  ctx.fillRect(-r, -r, r * 2, r * 2);
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  if (ang) { poly(ctx, [[-r * 0.95, -r * 0.95], [r * 0.95, -r * 0.95], [r * 0.95, r * 0.6], [0, r * 0.95], [-r * 0.95, r * 0.6]]); ctx.fill(); ctx.stroke(); }
  else { ctx.beginPath(); ctx.moveTo(-r * 0.9, -r * 0.9); ctx.lineTo(r * 0.9, -r * 0.9); ctx.lineTo(r * 0.9, r * 0.5); ctx.quadraticCurveTo(0, r, -r * 0.9, r * 0.5); ctx.closePath(); ctx.fill(); ctx.stroke(); }
  // Bay opening.
  ctx.fillStyle = "#10161c";
  ctx.fillRect(-r * 0.5, r * 0.2, r, r * 0.7);
  ctx.strokeStyle = pal.accent; ctx.lineWidth = r * 0.08;
  ctx.strokeRect(-r * 0.5, r * 0.2, r, r * 0.7);
  // Roof detail.
  ctx.fillStyle = pal.accent;
  ctx.fillRect(-r * 0.7, -r * 0.7, r * 1.4, r * 0.18);
}

function drawExtractor(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  if (ang) { poly(ctx, [[-r * 0.9, -r * 0.7], [r * 0.9, -r * 0.7], [r * 0.7, r * 0.9], [-r * 0.7, r * 0.9]]); ctx.fill(); ctx.stroke(); }
  else { ctx.beginPath(); ctx.arc(0, 0, r * 0.85, 0, 7); ctx.fill(); ctx.stroke(); }
  // Derrick / drill.
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.14;
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, r * 0.5); ctx.lineTo(0, -r * 0.9); ctx.lineTo(r * 0.5, r * 0.5); ctx.stroke();
  ctx.fillStyle = pal.accent;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.28, 0, 7); ctx.fill();
}

function drawSolar(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  ctx.fillStyle = pal.dark;
  ctx.fillRect(-r * 0.95, -r * 0.95, r * 1.9, r * 1.9);
  // Panel grid.
  ctx.fillStyle = ang ? "#2a3a55" : "#3a2a1a";
  ctx.fillRect(-r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
  ctx.strokeStyle = pal.accent; ctx.lineWidth = r * 0.06;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath(); ctx.moveTo(i * r * 0.55, -r * 0.85); ctx.lineTo(i * r * 0.55, r * 0.85);
    ctx.moveTo(-r * 0.85, i * r * 0.55); ctx.lineTo(r * 0.85, i * r * 0.55); ctx.stroke();
  }
  ctx.fillStyle = pal.glass;
  ctx.beginPath(); ctx.arc(r * 0.55, -r * 0.55, r * 0.16, 0, 7); ctx.fill();
}

function drawWind(ctx: CanvasRenderingContext2D, r: number, pal: Palette): void {
  ctx.fillStyle = pal.dark;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.35, 0, 7); ctx.fill();
  // Three blades.
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  for (let i = 0; i < 3; i++) {
    ctx.save();
    ctx.rotate((i / 3) * Math.PI * 2);
    poly(ctx, [[0, 0], [r * 1.0, -r * 0.22], [r * 1.1, 0]]);
    ctx.fill(); ctx.stroke();
    ctx.restore();
  }
  ctx.fillStyle = pal.accent;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.16, 0, 7); ctx.fill();
}

function drawConverter(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  if (ang) { poly(ctx, [[-r * 0.85, -r * 0.85], [r * 0.85, -r * 0.85], [r * 0.85, r * 0.85], [-r * 0.85, r * 0.85]]); ctx.fill(); ctx.stroke(); }
  else { ctx.beginPath(); ctx.arc(0, 0, r * 0.85, 0, 7); ctx.fill(); ctx.stroke(); }
  // Coils.
  ctx.strokeStyle = pal.accent; ctx.lineWidth = r * 0.12;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.5, 0, 7); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r * 0.28, 0, 7); ctx.stroke();
  ctx.fillStyle = pal.glass; ctx.beginPath(); ctx.arc(0, 0, r * 0.14, 0, 7); ctx.fill();
}

function drawStorage(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  if (ang) { poly(ctx, [[-r * 0.7, -r * 0.9], [r * 0.7, -r * 0.9], [r * 0.9, r * 0.9], [-r * 0.9, r * 0.9]]); ctx.fill(); ctx.stroke(); }
  else { ctx.beginPath(); ctx.arc(0, 0, r * 0.9, 0, 7); ctx.fill(); ctx.stroke(); }
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.1;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.55, 0, 7); ctx.stroke();
  ctx.fillStyle = pal.accent;
  ctx.fillRect(-r * 0.5, -r * 0.12, r, r * 0.24);
}

function drawTurret(ctx: CanvasRenderingContext2D, r: number, pal: Palette, ang: boolean): void {
  // Base.
  ctx.fillStyle = pal.dark;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.95, 0, 7); ctx.fill();
  ctx.fillStyle = pal.hull; ctx.strokeStyle = pal.dark;
  if (ang) { poly(ctx, [[-r * 0.5, -r * 0.5], [r * 0.5, -r * 0.5], [r * 0.5, r * 0.5], [-r * 0.5, r * 0.5]]); ctx.fill(); ctx.stroke(); }
  else { ctx.beginPath(); ctx.arc(0, 0, r * 0.55, 0, 7); ctx.fill(); ctx.stroke(); }
  // Barrel (aligned +x -> renderer rotates to heading).
  ctx.strokeStyle = pal.dark; ctx.lineWidth = r * 0.24;
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r * 1.5, 0); ctx.stroke();
  ctx.fillStyle = pal.tint; ctx.beginPath(); ctx.arc(0, 0, r * 0.22, 0, 7); ctx.fill();
}

// ---- Neutral features ----

function drawTree(ctx: CanvasRenderingContext2D, r: number): void {
  // Trunk.
  ctx.fillStyle = "#2c1f13";
  ctx.fillRect(-r * 0.13, r * 0.1, r * 0.26, r * 0.75);
  ctx.fillStyle = "#3a2a1a";
  ctx.fillRect(-r * 0.13, r * 0.1, r * 0.12, r * 0.75);
  // Layered canopy — darker base, mid, lit highlight for a rounded silhouette.
  ctx.fillStyle = "#234f2b"; ctx.strokeStyle = "#163019"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.arc(0, -r * 0.15, r * 0.82, 0, 7); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#2f6b3a";
  ctx.beginPath(); ctx.arc(r * 0.12, -r * 0.1, r * 0.58, 0, 7); ctx.fill();
  ctx.fillStyle = "#47954f";
  ctx.beginPath(); ctx.arc(-r * 0.26, -r * 0.42, r * 0.4, 0, 7); ctx.fill();
  ctx.fillStyle = "#5aa85f";
  ctx.beginPath(); ctx.arc(-r * 0.32, -r * 0.5, r * 0.18, 0, 7); ctx.fill();
}

function drawRock(ctx: CanvasRenderingContext2D, r: number): void {
  // Base mass with a cast-side shadow and two lit facets for volume.
  ctx.fillStyle = "#585d64"; ctx.strokeStyle = "#33383e"; ctx.lineWidth = 1;
  poly(ctx, [[-r * 0.8, r * 0.3], [-r * 0.3, -r * 0.7], [r * 0.5, -r * 0.5], [r * 0.85, r * 0.2], [r * 0.3, r * 0.8], [-r * 0.5, r * 0.7]]);
  ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#797f88";
  poly(ctx, [[-r * 0.3, -r * 0.7], [r * 0.5, -r * 0.5], [r * 0.1, r * 0.05], [-r * 0.35, -r * 0.1]]); ctx.fill();
  ctx.fillStyle = "#8f959d";
  poly(ctx, [[-r * 0.3, -r * 0.7], [r * 0.05, -r * 0.55], [-r * 0.15, -r * 0.15]]); ctx.fill();
  ctx.fillStyle = "#3f444b";
  poly(ctx, [[r * 0.5, -r * 0.5], [r * 0.85, r * 0.2], [r * 0.3, r * 0.8], [r * 0.1, r * 0.05]]); ctx.fill();
}

// ---- Strategic icons (icon zoom) ----

export function drawIcon(ctx: CanvasRenderingContext2D, role: Role, s: number, color: string): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, s * 0.18);
  switch (role) {
    case "commander":
      star(ctx, s); ctx.fill(); break;
    case "constructor":
      ctx.strokeRect(-s * 0.7, -s * 0.7, s * 1.4, s * 1.4); break;
    case "scout":
      tri(ctx, s * 0.7); ctx.fill(); break;
    case "raider":
      tri(ctx, s); ctx.fill(); break;
    case "assault":
      diamond(ctx, s); ctx.fill(); break;
    case "rocket":
      chevron(ctx, s); ctx.stroke(); break;
    case "tank":
      hexagon(ctx, s); ctx.fill(); break;
    case "aa":
      cross(ctx, s); break;
    case "sniper":
      narrowTri(ctx, s); ctx.fill(); break;
    case "artillery":
      ctx.beginPath(); ctx.arc(0, 0, s * 0.75, 0, 7); ctx.fill(); break;
    case "bomb":
      exMark(ctx, s); break;
    case "factory":
      ctx.strokeRect(-s * 0.85, -s * 0.85, s * 1.7, s * 1.7);
      ctx.beginPath(); ctx.arc(0, 0, s * 0.3, 0, 7); ctx.fill(); break;
    case "turret":
      ctx.beginPath(); ctx.arc(0, 0, s * 0.7, 0, 7); ctx.stroke();
      ctx.beginPath(); ctx.arc(0, 0, s * 0.25, 0, 7); ctx.fill(); break;
    default:
      // economy buildings: small filled square.
      ctx.fillRect(-s * 0.55, -s * 0.55, s * 1.1, s * 1.1); break;
  }
}

function star(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const rr = i % 2 === 0 ? s : s * 0.45;
    const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}
function tri(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.9, s * 0.7); ctx.lineTo(-s * 0.9, s * 0.7); ctx.closePath();
}
function narrowTri(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.45, s * 0.8); ctx.lineTo(-s * 0.45, s * 0.8); ctx.closePath();
}
function diamond(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s, 0); ctx.lineTo(0, s); ctx.lineTo(-s, 0); ctx.closePath();
}
function hexagon(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const x = Math.cos(a) * s, y = Math.sin(a) * s;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}
function chevron(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath(); ctx.moveTo(-s, s * 0.5); ctx.lineTo(0, -s * 0.5); ctx.lineTo(s, s * 0.5);
}
function cross(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.lineWidth = Math.max(1.5, s * 0.3);
  ctx.beginPath(); ctx.moveTo(-s, -s); ctx.lineTo(s, s); ctx.moveTo(s, -s); ctx.lineTo(-s, s); ctx.stroke();
}
function exMark(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.lineWidth = Math.max(1.5, s * 0.3);
  ctx.beginPath(); ctx.moveTo(-s * 0.7, -s * 0.7); ctx.lineTo(s * 0.7, s * 0.7);
  ctx.moveTo(s * 0.7, -s * 0.7); ctx.lineTo(-s * 0.7, s * 0.7); ctx.stroke();
}
